import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PromotionBanner } from '@barkinglabs/promotions-react';
import { PromotionConfigurationKind } from '@barkinglabs/promotions-types';
import styles from './QuizPromotionBanner.module.scss';
import * as events from '../../lib/analytics/events';
import { QUIZ_REFERRAL_COUPON_CODE, QuizReferral } from '../../lib/quizReferral';
import { useLocale } from '../../contexts/LocaleContext';
import { centsToDollarsNumber } from '../../lib/util';
import { maxTotalDiscountInCents, usePromoCodeDetails } from '../../hooks/usePromoCodeDetails';

interface QuizPromotionBannerProps {
  referral: QuizReferral;
  onClick?: () => void;
  onDismiss: () => void;
}

/**
 * The quiz-referred variant of the shop promo banner: same PromotionBanner
 * component the coupon promotions render through (see NanoBannerV2), fed the
 * personalized quiz offer instead of the fi-admin promotion text.
 */
export default function QuizPromotionBanner({ referral, onClick, onDismiss }: QuizPromotionBannerProps) {
  const { t } = useTranslation();
  const { wholeNumberCurrencyFormatter } = useLocale();
  const codeDetails = usePromoCodeDetails(QUIZ_REFERRAL_COUPON_CODE);
  const discountCents = codeDetails ? maxTotalDiscountInCents(codeDetails) : 0;

  const visible = discountCents > 0;
  useEffect(() => {
    if (visible) {
      events.quizPdpBannerShown();
    }
  }, [visible]);

  // Details still loading, or the code has no fixed-amount discount in this
  // currency — the parent falls back to the regular promotion banner.
  if (!visible) {
    return null;
  }

  const petName = referral.petName ?? (referral.species === 'cat' ? t('your_cat') : t('your_dog'));
  const title = t('quiz_promo_banner_offer', {
    amount: wholeNumberCurrencyFormatter.format(centsToDollarsNumber(discountCents)),
    petName,
    couponCode: QUIZ_REFERRAL_COUPON_CODE,
  });

  return (
    <div className={styles.container} data-testid="quiz-promo-banner">
      <div onClick={onClick}>
        <PromotionBanner
          className={'promotionBanner'}
          promotion={{
            configuration: {
              kind: PromotionConfigurationKind.SIMPLE,
              title,
              subtitle: undefined,
              couponCode: undefined,
              countdown: 'none',
              extraCss: undefined,
              imageUrl: undefined,
              backgroundImageUrl: undefined,
            },
          }}
          promotionEnd={''}
        />
      </div>
      <button
        type="button"
        className={styles.dismissButton}
        aria-label={t('dismiss')}
        data-testid="quiz-promo-banner-dismiss"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
          events.quizPdpBannerDismissed();
        }}
      >
        <span className={styles.dismissIcon} />
      </button>
    </div>
  );
}
